import { useState, useEffect, useRef } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function SalesDashboard() {
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'year'>('month');
  const [salesData, setSalesData] = useState<any>(null);
  const [previousData, setPreviousData] = useState<any>(null);
  const [topProducts, setTopProducts] = useState<any[]>([]);
  const [topCustomers, setTopCustomers] = useState<any[]>([]);
  const [monthlySales, setMonthlySales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const dashboardRef = useRef<HTMLDivElement>(null);

  // Format monétaire en GNF (change 'fr-GN' en 'fr-FR' et 'GNF' en '€' si besoin)
  const formatCurrency = (value: number) => {
    return value.toLocaleString('fr-GN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + ' GNF';
  };

  useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);

      const [
        currentRes,
        prevRes,
        productsRes,
        customersRes,
        monthlyRes
      ] = await Promise.all([
        getResources(`sales/current?period=${period}`),
        getResources(`sales/previous?period=${period}`),
        getResources(`sales/top-products`),
        getResources(`sales/top-customers`),
        getResources(`sales/monthly-evolution`),
      ]);

      setSalesData(currentRes.data ?? { revenue: 0, orders: 0, avgOrder: 0 });
      setPreviousData(prevRes.data ?? { revenue: 0, orders: 0, avgOrder: 0 });

      setTopProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setTopCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setMonthlySales(Array.isArray(monthlyRes.data) ? monthlyRes.data : []);

    } catch (error) {
      console.error('Erreur chargement données ventes:', error);

      // fallback MINIMAL
      setSalesData({ revenue: 0, orders: 0, avgOrder: 0 });
      setPreviousData({ revenue: 0, orders: 0, avgOrder: 0 });
      setTopProducts([]);
      setTopCustomers([]);
      setMonthlySales([]);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [period]);


  const calculateGrowth = (current: number, previous: number) => {
    if (!previous || previous === 0) return '0.0';
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const maxRevenue = monthlySales.length > 0 
    ? Math.max(...monthlySales.map(m => m.revenue))
    : 1;

  // Export Excel
  const exportToExcel = () => {
    const data = [
      ['TABLEAU DE BORD DES VENTES'],
      [`Période: ${period === 'today' ? 'Aujourd\'hui' : period === 'week' ? 'Cette semaine' : period === 'month' ? 'Ce mois' : 'Cette année'}`],
      [new Date().toLocaleDateString('fr-FR')],
      [],
      ['STATISTIQUES GÉNÉRALES'],
      ['Chiffre d\'Affaires', formatCurrency(salesData?.revenue || 0)],
      ['Nombre de Commandes', salesData?.orders || 0],
      ['Panier Moyen', formatCurrency(salesData?.avgOrder || 0)],
      [],
      ['TOP 5 PRODUITS'],
      ['Produit', 'Ventes', 'CA Généré'],
      ...topProducts.map(p => [p.name, p.sales, formatCurrency(p.revenue)]),
      [],
      ['TOP 5 CLIENTS'],
      ['Client', 'Commandes', 'CA Généré'],
      ...topCustomers.map(c => [c.name, c.orders, formatCurrency(c.revenue)]),
      [],
      ['ÉVOLUTION MENSUELLE'],
      ['Mois', 'Chiffre d\'Affaires'],
      ...monthlySales.map(m => [m.month, formatCurrency(m.revenue)])
    ];

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dashboard Ventes');
    
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, `ventes_dashboard_${period}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // Export PDF
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;

    const canvas = await html2canvas(dashboardRef.current, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height],
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save(`ventes_dashboard_${period}_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500 text-lg">Chargement du tableau de bord...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" ref={dashboardRef}>
      {/* En-tête + boutons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tableau de Bord des Ventes</h2>
          <p className="text-sm text-gray-600 mt-1">
            Période sélectionnée : <span className="font-medium">
              {period === 'today' ? 'Aujourd\'hui' : 
               period === 'week' ? 'Cette semaine' : 
               period === 'month' ? 'Ce mois-ci' : 'Cette année'}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="today">Aujourd'hui</option>
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>

          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
          >
            <i className="ri-file-pdf-line"></i>
            Export PDF
          </button>

          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
          >
            <i className="ri-file-excel-line"></i>
            Export Excel
          </button>
        </div>
      </div>

      {/* Cartes KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <i className="ri-money-euro-circle-line text-2xl"></i>
            </div>
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
              parseFloat(calculateGrowth(salesData?.revenue || 0, previousData?.revenue || 1)) >= 0
                ? 'bg-green-400 bg-opacity-30'
                : 'bg-red-400 bg-opacity-30'
            }`}>
              {parseFloat(calculateGrowth(salesData?.revenue || 0, previousData?.revenue || 1)) >= 0 ? '+' : ''}
              {calculateGrowth(salesData?.revenue || 0, previousData?.revenue || 1)}%
            </span>
          </div>
          <p className="text-sm opacity-90 mb-1">Chiffre d'Affaires</p>
          <p className="text-3xl font-bold">{formatCurrency(salesData?.revenue || 0)}</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <i className="ri-shopping-cart-line text-2xl"></i>
            </div>
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
              parseFloat(calculateGrowth(salesData?.orders || 0, previousData?.orders || 1)) >= 0
                ? 'bg-green-400 bg-opacity-30'
                : 'bg-red-400 bg-opacity-30'
            }`}>
              {parseFloat(calculateGrowth(salesData?.orders || 0, previousData?.orders || 1)) >= 0 ? '+' : ''}
              {calculateGrowth(salesData?.orders || 0, previousData?.orders || 1)}%
            </span>
          </div>
          <p className="text-sm opacity-90 mb-1">Nombre de Commandes</p>
          <p className="text-3xl font-bold">{salesData?.orders || 0}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <i className="ri-bar-chart-box-line text-2xl"></i>
            </div>
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
              parseFloat(calculateGrowth(salesData?.avgOrder || 0, previousData?.avgOrder || 1)) >= 0
                ? 'bg-green-400 bg-opacity-30'
                : 'bg-red-400 bg-opacity-30'
            }`}>
              {parseFloat(calculateGrowth(salesData?.avgOrder || 0, previousData?.avgOrder || 1)) >= 0 ? '+' : ''}
              {calculateGrowth(salesData?.avgOrder || 0, previousData?.avgOrder || 1)}%
            </span>
          </div>
          <p className="text-sm opacity-90 mb-1">Panier Moyen</p>
          <p className="text-3xl font-bold">{formatCurrency(salesData?.avgOrder || 0)}</p>
        </div>
      </div>

      {/* Top Produits & Clients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Produits</h3>
          <div className="space-y-4">
            {topProducts.map((product, i) => (
              <div key={i} className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{product.name}</p>
                  <p className="text-sm text-gray-500">{product.sales} ventes</p>
                </div>
                <p className="font-semibold text-gray-900">{formatCurrency(product.revenue)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Clients</h3>
          <div className="space-y-4">
            {topCustomers.map((customer, i) => (
              <div key={i} className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{customer.name}</p>
                  <p className="text-sm text-gray-500">{customer.orders} commandes</p>
                </div>
                <p className="font-semibold text-gray-900">{formatCurrency(customer.revenue)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Évolution mensuelle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Évolution Mensuelle du CA</h3>
        <div className="space-y-4">
          {monthlySales.map((data, i) => (
            <div key={i} className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-600 w-16">{data.month}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-10 relative overflow-hidden">
                <div
                  className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-end pr-4 text-white font-medium text-sm transition-all duration-700"
                  style={{ width: `${(data.revenue / maxRevenue) * 100}%` }}
                >
                  {formatCurrency(data.revenue)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}